import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSignature, History, Trash2, Upload } from "lucide-react";
import apiClient from "@/api/client";
import { uploadErrorMessage, uploadManagedFile } from "@/utils/managedFile";
import { PageLoader, Spinner } from "@/components/ui/Spinner";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { usePageTitle } from "@/hooks/usePageTitle";

/** Domyślne wzory umów dla etapów projektowych ZE (PT / kosztorys / MDCP).
 *
 *  Ustawia je kadra dyrektorska (dyrektor / CEO); reszta widzi stan bez przycisków.
 *  Ustawiony wzór dopina się automatycznie do każdego NOWEGO postępowania ofertowego
 *  wystawianego z panelu etapu — jako załącznik „Wzór umowy". To domyślka: kupujący
 *  może zdjąć załącznik ze szkicu ogłoszenia przed publikacją. Podmiana wzoru nie
 *  zmienia ogłoszeń już założonych. */

interface TemplateFile {
  id: string;
  original_filename?: string;
  url: string | null;
}

interface TemplateVersion {
  file: TemplateFile | null;
  from: string;
  to: string | null;
  by: string | null;
}

interface TemplateRow {
  path: string;
  path_label: string;
  file: TemplateFile | null;
  updated_at: string | null;
  updated_by: string | null;
  /** Dziennik wersji od najnowszej — „od–do, jaki plik, kto"; file=null to zdjęcie wzoru. */
  history: TemplateVersion[];
}

interface TemplatesState {
  templates: TemplateRow[];
  can_manage: boolean;
}

const BASE = "/power-projects/stage-contract-templates/";
const KEY = ["pp", "stage-contract-templates"] as const;

export function ContractTemplatesPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: KEY,
    queryFn: () => apiClient.get<{ data: TemplatesState }>(BASE).then((r) => r.data.data),
  });
  const inval = () => qc.invalidateQueries({ queryKey: KEY });

  const uploadM = useMutation({
    mutationFn: async ({ path, file }: { path: string; file: File }) => {
      const uploaded = await uploadManagedFile(file);
      return apiClient.put(BASE, { path, managed_file: uploaded.id });
    },
    onSuccess: inval,
  });
  const removeM = useMutation({
    mutationFn: (path: string) => apiClient.delete(`${BASE}?path=${path}`),
    onSuccess: inval,
  });

  usePageTitle("Wzory umów — etapy ZE");
  if (isLoading || !data) return <PageLoader />;

  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-2xl font-bold text-gray-900 dark:text-gray-100">
        Wzory umów — etapy ZE
      </h1>
      <div className="mb-4 h-0.5 w-8 bg-[var(--color-primary)]" />
      <p className="mb-4 text-sm text-[var(--color-text-secondary)]">
        Ustawiony wzór trafia jako załącznik <b>„Wzór umowy”</b> do każdego nowego postępowania
        na dany etap — oferent widzi warunki, zanim poda cenę. Kupujący może zdjąć załącznik ze
        szkicu przed publikacją. Każda podmiana to nowa wersja: ogłoszenia trzymają plik z chwili
        założenia, a poniżej widać, kiedy która wersja obowiązywała.
      </p>
      {!data.can_manage && (
        <p className="mb-4 text-xs text-[var(--color-text-muted)]">
          Wzory ustawia kadra dyrektorska — Ty widzisz stan bez możliwości zmiany.
        </p>
      )}
      <ul className="space-y-3">
        {data.templates.map((row) => (
          <li
            key={row.path}
            className="flex flex-wrap items-start gap-3 border border-[var(--color-border)] bg-white p-4 dark:bg-gray-800"
          >
            <FileSignature size={18} className="shrink-0 text-[var(--color-primary)]" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[var(--color-text-primary)]">
                {row.path_label}
              </div>
              {row.file ? (
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-[var(--color-text-muted)]">
                  <ManagedFileLink file={row.file}>{row.file.original_filename ?? "plik"}</ManagedFileLink>
                  {row.updated_at && (
                    <span>
                      · {new Date(row.updated_at).toLocaleDateString("pl")}
                      {row.updated_by ? ` · ${row.updated_by}` : ""}
                    </span>
                  )}
                </div>
              ) : (
                <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                  Brak wzoru — postępowania na ten etap wychodzą bez załącznika „Wzór umowy”.
                </div>
              )}
            </div>
            {data.can_manage && (
              <div className="flex items-center gap-2 self-start">
                <label
                  className={`inline-flex cursor-pointer items-center gap-1.5 border border-[var(--color-border)] px-3 py-1.5 text-xs hover:bg-[var(--color-bg-elevated)] ${
                    uploadM.isPending ? "pointer-events-none opacity-60" : ""
                  }`}
                >
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.odt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadM.mutate({ path: row.path, file });
                      e.target.value = "";
                    }}
                  />
                  {uploadM.isPending && uploadM.variables?.path === row.path ? <Spinner /> : <Upload size={13} />}
                  {row.file ? "Podmień wzór" : "Wgraj wzór"}
                </label>
                {row.file && (
                  <button
                    type="button"
                    title="Zdejmij wzór (nowe postępowania pójdą bez wzoru umowy)"
                    disabled={removeM.isPending}
                    onClick={() => removeM.mutate(row.path)}
                    className="p-1.5 text-[var(--color-text-muted)] hover:text-red-600"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            )}
            {/* The spinner just stopped before — the upload failed with no word of it. */}
            {uploadM.isError && uploadM.variables?.path === row.path && (
              <p className="w-full text-xs text-red-600 dark:text-red-400">
                {uploadErrorMessage(uploadM.error, "Nie udało się wgrać wzoru — spróbuj ponownie.")}
              </p>
            )}
            {row.history.length > 0 && (
              <div className="w-full border-t border-[var(--color-border)] pt-2">
                <div className="mb-1 flex items-center gap-1 font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--color-text-muted)]">
                  <History size={11} /> Historia wersji
                </div>
                <ul className="space-y-0.5">
                  {row.history.map((v, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-x-2 text-xs">
                      <span className="font-mono text-[11px] text-[var(--color-text-muted)]">
                        {new Date(v.from).toLocaleDateString("pl")} –{" "}
                        {v.to ? new Date(v.to).toLocaleDateString("pl") : "nadal"}
                      </span>
                      {v.file ? (
                        <ManagedFileLink file={v.file}>{v.file.original_filename ?? "plik"}</ManagedFileLink>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">— wzór zdjęty —</span>
                      )}
                      {v.by && <span className="text-[11px] text-[var(--color-text-muted)]">· {v.by}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
